import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Grid,
  Divider,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Assignment as AssignmentIcon,
  Business as BusinessIcon,
  AccessTime as AccessTimeIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Notifications as NotificationsIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";

import { getToken, base_url } from "../../config/config";

// Child Components
import ANRModal from "./KanbanComponents/ANRModal";
import TaskFormModal from "./KanbanComponents/TaskFormModal";
import TaskCard from "./KanbanComponents/TaskCard";
import Column from "./KanbanComponents/Column";
import TaskBarcodeModal from "./KanbanComponents/TaskBarcodeModal";
import TaskPrintModal from "./KanbanComponents/TaskPrintModal";
import { Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

/******************************************************
 * THEME COLORS
 ******************************************************/
const lightThemeColors = {
  primary: "#4361ee",
  secondary: "#3a0ca3",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#f8f9fd",
  card: "#ffffff",
  text: "#2d3748",
  textSecondary: "#718096",
  columnHeader: "#ebf3ff",
  columnBackground: "#ffffff",
  columnBackgroundHover: "#f7faff",
  taskBorder: "#e2e8f0",
};

const darkThemeColors = {
  primary: "#4361ee",
  secondary: "#7b5cff",
  success: "#2ecc71",
  warning: "#f39c12",
  error: "#e74c3c",
  info: "#3498db",
  background: "#1a202c",
  card: "#2d3748",
  text: "#f7fafc",
  textSecondary: "#a0aec0",
  columnHeader: "#2d3748",
  columnBackground: "#2d3748",
  columnBackgroundHover: "#3a4556",
  taskBorder: "#4a5568",
};

/******************************************************
 * STATUS <-> COLUMN UTILS
 ******************************************************/
const statusToColumn = (status) => {
  // Unified status values map directly to column ids
  return status; // "assigned", "in-progress", "completed", "archived"
};

const columnToStatus = (column) => {
  // Column ids are the same as status values
  return column; // "assigned", "in-progress", "completed", "archived"
};

const KanbanPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isDark = theme.palette.mode === "dark";
  const [isDarkMode, setIsDarkMode] = useState(isDark);
  const themeColors = isDarkMode ? darkThemeColors : lightThemeColors;

  // Logged-in user info
  const userState = useSelector((state) => state.user);
  const loggedInUserId = userState?.user?._id || "";
  const loggedInUserType = userState?.user?.userType || "user";

  // Kanban columns - using unified status values
  const [columns] = useState([
    { id: "assigned", title: "Zugewiesen" },
    { id: "in-progress", title: "In Bearbeitung" },
    { id: "completed", title: "Abgeschlossen" },
    { id: "archived", title: "Archiviert" },
  ]);

  // Collapsed columns state
  const [collapsedColumns, setCollapsedColumns] = useState([]);

  // Main tasks state
  const [tasks, setTasks] = useState([]);
  const [allTasks, setAllTasks] = useState([]);

  // Modals
  const [taskFormOpen, setTaskFormOpen] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [currentColumnId, setCurrentColumnId] = useState("assigned");
  const [anrModalOpen, setAnrModalOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
  const [selectedTaskForBarcode, setSelectedTaskForBarcode] = useState(null);
  const [printModalOpen, setPrintModalOpen] = useState(false);

  // We store all users (both Admin + Employee) here
  const [allUsers, setAllUsers] = useState([]);

  // By default, show the logged-in user's tasks
  const [selectedUserFilter, setSelectedUserFilter] = useState(loggedInUserId);

  // Email context - for creating tasks from emails
  const [emailContext, setEmailContext] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const token = getToken();

  /******************************************************
   * COLUMN COLLAPSE HANDLER
   ******************************************************/
  const toggleColumnCollapse = (columnId) => {
    setCollapsedColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId],
    );
  };

  /******************************************************
   * 1) FETCH USERS & TASKS
   ******************************************************/
  useEffect(() => {
    fetchAllUsers();
  }, []);

  useEffect(() => {
    setIsDarkMode(isDark);
  }, [isDark]);

  useEffect(() => {
    fetchTasks();
  }, [selectedUserFilter]);

  // Handle email-based task creation from navigation
  useEffect(() => {
    if (location.state?.createFromEmail && location.state?.emailData) {
      const { emailData } = location.state;
      // Open task form with email context
      handleAddTask("assigned", emailData);
      // Clear navigation state to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);

  /**
   * fetchAllUsers merges Admin + Employee
   * Adjust based on how your backend actually returns data
   */
  const fetchAllUsers = async () => {
    try {
      // 1) getAllAdmins
      const adminRes = await axios.get(`${base_url}/v1/getAllAdmins`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // 2) getAllEmployees
      const empRes = await axios.get(`${base_url}/v1/getAllEmployees`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // For example, if getAllAdmins returns: { status:1, users: [...] }
      // And getAllEmployees returns an array of employees
      // *Adjust exactly to your real response shape*
      const adminsRaw = adminRes.data; // see what you get
      const empsRaw = empRes.data; // see what you get

      // Suppose your admin endpoint: { users: [ { _id, firstName, lastName }, ... ] }
      // or if it's { data: [ ... ] } or { data: { ... } } etc.

      const adminList = Array.isArray(adminsRaw?.message?.users)
        ? adminsRaw.message.users
        : [];
      console.log("adminList", adminList);
      // Suppose employees is an array. If it's not, adjust the shape
      const employeeList = Array.isArray(empsRaw) ? empsRaw : [];

      // Map them to unify shape
      const admins = adminList.map((adm) => ({
        _id: adm._id,
        displayName: `${adm.firstName} ${adm.lastName}`,
        userModel: "Admin",
      }));

      const employees = employeeList
        .filter((emp) => emp.isActive === true)
        .map((emp) => ({
          _id: emp._id,
          displayName: emp.name,
          userModel: "Employee",
        }));

      // Combine
      const allusers = [...admins, ...employees];
      setAllUsers(allusers);
      console.log("allUsers", allusers);
    } catch (err) {
      console.error("Error fetching all users:", err);
      toast.error("Fehler beim Laden der Benutzer");
    }
  };

  // GET tasks from /getAllAufgabe, filtered by user if selected
  const fetchTasks = async () => {
    try {
      const params = { isTask: true };
      if (selectedUserFilter) {
        params.user = selectedUserFilter; // fetch tasks for that user
      }
      const response = await axios.get(`${base_url}/v1/getAllAufgabe`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      // If the response is an array
      const data = Array.isArray(response.data) ? response.data : [];
      const mapped = data.map((t) => ({
        ...t,
        id: t._id,
        column: statusToColumn(t.status),
      }));
      setTasks(mapped);
      setAllTasks(mapped);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Fehler beim Laden der Aufgaben");
    }
  };

  /******************************************************
   * 2) CREATE / UPDATE TASK
   ******************************************************/
  const handleAddTask = (columnId, emailData = null) => {
    setCurrentTask(null);
    setCurrentColumnId(columnId || "assigned");
    setEmailContext(emailData);
    setTaskFormOpen(true);
  };

  const handleEditTask = (task) => {
    setCurrentTask(task);
    setCurrentColumnId(task.column);
    setEmailContext(null);
    setTaskFormOpen(true);
  };

  /**
   * Called by TaskFormModal => onSave
   * formData has { assignedTo, ... }
   */
  const handleSaveTask = async (formData, isEdit) => {
    try {
      if (isEdit) {
        await updateTask(formData);
      } else {
        await createNewTask(formData);
      }
    } catch (err) {
      console.error("Error saving task:", err);
      toast.error("Fehler beim Speichern der Aufgabe");
    }
  };

  /**
   * Creates a new task in DB
   * Now supports both project and email tasks
   * Reads from the unified Aufgabe model
   */
  const createNewTask = async (taskData) => {
    const status = columnToStatus(taskData.column);

    // If user picked an "assignedTo" from allUsers, use that
    let assignedId = loggedInUserId;
    let assignedModel = loggedInUserType === "employee" ? "Employee" : "Admin";

    if (taskData.assignedTo && taskData.assignedTo._id) {
      assignedId = taskData.assignedTo._id;
      assignedModel = taskData.assignedTo.userModel;
    }

    const payload = {
      title: taskData.title,
      description: taskData.description,
      status,
      isTask: true,
      user: assignedId,
      userModel: assignedModel,
      projectId: taskData.projectId || null,
      assignedDate: taskData.assignedDate || null,
      deadline: taskData.deadline || null,
      // Email-related fields (if this is an email task)
      emailId: emailContext?.emailId || taskData.emailId || null,
      messageId: emailContext?.messageId || taskData.messageId || null,
      emailAttachments:
        emailContext?.emailAttachments || taskData.emailAttachments || [],
      hours: taskData.hours || 0,
    };

    const res = await axios.post(`${base_url}/v1/createAufgabe`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    const newTask = res.data;
    const mapped = {
      ...newTask,
      id: newTask._id,
      column: statusToColumn(newTask.status),
    };
    setTasks((prev) => [...prev, mapped]);
    setEmailContext(null);
    toast.success("Neue Aufgabe erstellt!");
    setTaskFormOpen(false);
  };

  /**
   * Updates an existing task
   * Supports both project and email task updates
   */
  const updateTask = async (taskData) => {
    const status = columnToStatus(taskData.column);

    // If user chose assignedTo in the modal
    // fallback to the old user if not set
    let assignedId = taskData.user;
    let assignedModel = taskData.userModel;
    if (taskData.assignedTo && taskData.assignedTo._id) {
      assignedId = taskData.assignedTo._id;
      assignedModel = taskData.assignedTo.userModel;
    }

    const payload = {
      title: taskData.title,
      description: taskData.description,
      status,
      user: assignedId,
      userModel: assignedModel,
      assignedDate: taskData.assignedDate,
      deadline: taskData.deadline,
      // Email-related fields can be updated too
      hours: taskData.hours || 0,
      isArchived: status === "archived",
    };

    const res = await axios.put(
      `${base_url}/v1/updateAufgabe/${taskData.id}`,
      payload,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );

    const updated = res.data;
    const updatedMapped = {
      ...updated,
      id: updated._id,
      column: statusToColumn(updated.status),
    };
    setTasks((prev) =>
      prev.map((t) => (t.id === taskData.id ? updatedMapped : t)),
    );
    toast.success("Aufgabe aktualisiert!");
    setTaskFormOpen(false);
  };

  /******************************************************
   * 3) MOVE TASK (DRAG & DROP)
   ******************************************************/
  const moveTask = async (taskId, fromColumn, toColumn) => {
    if (fromColumn === toColumn) return;

    // immediate local update
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, column: toColumn } : t)),
    );

    try {
      const status = columnToStatus(toColumn);
      await axios.put(
        `${base_url}/v1/updateAufgabe/${taskId}`,
        { status },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(`Aufgabe nach "${toColumn}" verschoben.`);
    } catch (err) {
      console.error("Error moving task:", err);
      toast.error("Fehler beim Aktualisieren der Aufgabe");
      // revert local
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, column: fromColumn } : t)),
      );
    }
  };

  /******************************************************
   * 4) DELETE TASK
   ******************************************************/
  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${base_url}/v1/deleteAufgabe/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.info("Aufgabe gelöscht");
    } catch (err) {
      console.error("Error deleting task:", err);
      toast.error("Fehler beim Löschen der Aufgabe");
    }
  };

  /******************************************************
   * 4.1) ARCHIVE TASK
   ******************************************************/
  const handleArchiveTask = async (taskId) => {
    try {
      await axios.put(
        `${base_url}/v1/archiveAufgabe/${taskId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      // Remove task from the board
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.info("Aufgabe archiviert");
    } catch (err) {
      console.error("Error archiving task:", err);
      toast.error("Fehler beim Archivieren der Aufgabe");
    }
  };

  /******************************************************
   * 4.2) START TIMER
   ******************************************************/
  const handleStartTimer = async (taskId) => {
    try {
      const response = await axios.put(
        `${base_url}/v1/startTaskTimer/${taskId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data && response.data.task) {
        const updatedTask = response.data.task;
        const newColumn = statusToColumn(updatedTask.status);

        // Update the task in the state with the new data
        setTasks((prev) =>
          prev.map((t) =>
            t._id === taskId || t.id === taskId
              ? {
                  ...t,
                  ...updatedTask,
                  id: updatedTask._id,
                  column: newColumn,
                }
              : t,
          ),
        );

        // If we have the barcode modal open, update the selected task too
        if (
          selectedTaskForBarcode &&
          (selectedTaskForBarcode._id === taskId ||
            selectedTaskForBarcode.id === taskId)
        ) {
          setSelectedTaskForBarcode((prev) => ({
            ...prev,
            ...updatedTask,
            id: updatedTask._id,
            column: newColumn,
          }));
        }

        toast.success("Timer gestartet");
      }
    } catch (err) {
      console.error("Error starting timer:", err);
      toast.error("Fehler beim Starten des Timers");
    }
  };

  /******************************************************
   * 4.3) STOP TIMER
   ******************************************************/
  const handleStopTimer = async (taskId) => {
    try {
      const response = await axios.put(
        `${base_url}/v1/stopTaskTimer/${taskId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data && response.data.task) {
        const updatedTask = response.data.task;
        const newColumn = statusToColumn(updatedTask.status);

        // Update the task in the state with the new data
        setTasks((prev) =>
          prev.map((t) =>
            t._id === taskId || t.id === taskId
              ? {
                  ...t,
                  ...updatedTask,
                  id: updatedTask._id,
                  column: newColumn,
                }
              : t,
          ),
        );

        // If we have the barcode modal open, update the selected task too
        if (
          selectedTaskForBarcode &&
          (selectedTaskForBarcode._id === taskId ||
            selectedTaskForBarcode.id === taskId)
        ) {
          setSelectedTaskForBarcode((prev) => ({
            ...prev,
            ...updatedTask,
            id: updatedTask._id,
            column: newColumn,
          }));
        }

        toast.success("Timer gestoppt");
      }
    } catch (err) {
      console.error("Error stopping timer:", err);
      toast.error("Fehler beim Stoppen des Timers");
    }
  };

  /******************************************************
   * 4.4) RESET TIMER
   ******************************************************/
  const handleResetTimer = async (taskId) => {
    try {
      const response = await axios.put(
        `${base_url}/v1/resetTaskTimer/${taskId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data && response.data.task) {
        const updatedTask = response.data.task;
        const newColumn = statusToColumn(updatedTask.status);

        // Update the task in the state with the new data
        setTasks((prev) =>
          prev.map((t) =>
            t._id === taskId || t.id === taskId
              ? {
                  ...t,
                  ...updatedTask,
                  id: updatedTask._id,
                  column: newColumn,
                }
              : t,
          ),
        );

        // If we have the barcode modal open, update the selected task too
        if (
          selectedTaskForBarcode &&
          (selectedTaskForBarcode._id === taskId ||
            selectedTaskForBarcode.id === taskId)
        ) {
          setSelectedTaskForBarcode((prev) => ({
            ...prev,
            ...updatedTask,
            id: updatedTask._id,
            column: newColumn,
          }));
        }

        toast.success("Timer zurückgesetzt");
      }
    } catch (err) {
      console.error("Error resetting timer:", err);
      toast.error("Fehler beim Zurücksetzen des Timers");
    }
  };

  /******************************************************
   * 5) SUBTASKS
   ******************************************************/
  const createSubTask = async (aufgabeId, subTaskText) => {
    try {
      const res = await axios.post(
        `${base_url}/v1/createSubTasks`,
        { aufgabeId, task: subTaskText },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data.data;
      setTasks((prev) =>
        prev.map((t) => (t.id === updated._id ? { ...t, ...updated } : t)),
      );
      toast.success("Subtask hinzugefügt");
    } catch (err) {
      console.error("Error creating subtask:", err);
      toast.error("Fehler beim Hinzufügen des Subtasks");
    }
  };

  const updateSubTask = async (aufgabeId, subTaskId, newStatus) => {
    try {
      const res = await axios.put(
        `${base_url}/v1/updateSubTask`,
        { aufgabeId, subTaskId, status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const updated = res.data.data;
      setTasks((prev) =>
        prev.map((t) => (t.id === updated._id ? { ...t, ...updated } : t)),
      );
      toast.success("Subtask aktualisiert");
    } catch (err) {
      console.error("Error updating subtask:", err);
      toast.error("Fehler beim Aktualisieren des Subtasks");
    }
  };

  /******************************************************
   * 6) ANR
   ******************************************************/
  const handleOpenAnr = (taskId) => {
    setSelectedTaskId(taskId);
    setAnrModalOpen(true);
  };
  const handleAnrSelect = async (anrData) => {
    try {
      // Call the updateAufgabe API with projectId in the body
      const response = await axios.put(
        `${base_url}/v1/updateAufgabe/${selectedTaskId}`,
        {
          projectId: anrData.projectId, // Send the project ID from anrData
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      console.log("ANR Update Response:", response);

      // Handle both response.data.task and direct response.data as the task object
      const updatedTask = response.data.task || response.data;

      if (updatedTask && updatedTask._id) {
        // Update local state with the updated task
        const taskToUpdate = {
          ...updatedTask,
          id: updatedTask._id,
          column: statusToColumn(updatedTask.status),
        };

        setTasks((prev) =>
          prev.map((t) =>
            t._id === selectedTaskId || t.id === selectedTaskId
              ? taskToUpdate
              : t,
          ),
        );

        setAllTasks((prev) =>
          prev.map((t) =>
            t._id === selectedTaskId || t.id === selectedTaskId
              ? taskToUpdate
              : t,
          ),
        );

        setSelectedTaskId(null);
        setAnrModalOpen(false);
        toast.success("ANR erfolgreich zugewiesen!");
      } else {
        console.error("Invalid response structure:", response.data);
        toast.error("Ungültige Antwort vom Server");
      }
    } catch (err) {
      console.error("Error assigning ANR:", err);
      toast.error("Fehler beim Zuweisen der ANR");
    }
  };

  /******************************************************
   * 6.1) Comments
   ******************************************************/
  // Add Comment to Task
  const handleAddComment = async (taskId, commentText) => {
    try {
      if (!commentText.trim()) return null;

      const response = await axios.post(
        `${base_url}/v1/addTaskComment`,
        {
          aufgabeId: taskId,
          text: commentText.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data && response.data.task) {
        // Update the task in the state with the new data
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  comments: response.data.task.comments,
                }
              : t,
          ),
        );
        toast.success("Kommentar hinzugefügt");
        return response.data.comment;
      }

      return null;
    } catch (err) {
      console.error("Error adding comment:", err);
      toast.error("Fehler beim Hinzufügen des Kommentars");
      return null;
    }
  };

  // Edit Comment
  const handleEditComment = async (taskId, commentId, newText) => {
    try {
      if (!newText.trim()) return false;

      const response = await axios.put(
        `${base_url}/v1/editTaskComment`,
        {
          aufgabeId: taskId,
          commentId: commentId,
          text: newText.trim(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data && response.data.task) {
        // Update the task in the state with the new data
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  comments: response.data.task.comments,
                }
              : t,
          ),
        );
        toast.success("Kommentar aktualisiert");
        return true;
      }

      return false;
    } catch (err) {
      console.error("Error editing comment:", err);
      toast.error("Fehler beim Bearbeiten des Kommentars");
      return false;
    }
  };

  // Delete Comment
  const handleDeleteComment = async (taskId, commentId) => {
    try {
      const response = await axios.delete(
        `${base_url}/v1/deleteTaskComment/${taskId}/${commentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data && response.data.task) {
        // Update the task in the state with the new data
        setTasks((prev) =>
          prev.map((t) =>
            t.id === taskId
              ? {
                  ...t,
                  comments: response.data.task.comments,
                }
              : t,
          ),
        );
        toast.info("Kommentar gelöscht");
        return true;
      }

      return false;
    } catch (err) {
      console.error("Error deleting comment:", err);
      toast.error("Fehler beim Löschen des Kommentars");
      return false;
    }
  };

  /******************************************************
   * 7) Barcode Handling
   ******************************************************/
  const handleShowBarcode = (task) => {
    setSelectedTaskForBarcode(task);
    setBarcodeModalOpen(true);
  };

  /******************************************************
   * 8) STATS
   ******************************************************/
  const totalTasks = tasks.length;
  const tasksWithAnr = tasks.filter(
    (t) => t.projectId && t.projectId.ANR,
  ).length;
  const totalHours = tasks.reduce((sum, t) => sum + (t.hours || 0), 0);
  const selfAssignedTasks = tasks.filter(
    (t) => t.user === loggedInUserId,
  ).length;

  // Filter user => re-fetch tasks
  const handleUserFilterChange = (e) => {
    setSelectedUserFilter(e.target.value);
  };

  // Search handler
  const handleSearch = async (e) => {
    const query = e.target.value;

    if (!query) {
      setTasks(allTasks);
      return;
    }

    try {
      // Instead of filtering client-side, fetch from the backend with search parameter
      const params = { isTask: true, search: query };
      if (selectedUserFilter) {
        params.user = selectedUserFilter; // maintain user filter
      }

      const response = await axios.get(`${base_url}/v1/getAllAufgabe`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      // If the response is an array
      const data = Array.isArray(response.data) ? response.data : [];
      const mapped = data.map((t) => ({
        ...t,
        id: t._id,
        column: statusToColumn(t.status),
      }));
      setTasks(mapped);
    } catch (error) {
      console.error("Error searching tasks:", error);
      toast.error("Fehler bei der Suche nach Aufgaben");
    }
  };

  // Handle Calendar view
  const handleCalendarView = () => {
    // Navigate to calendar view (if you have a route for it)
    navigate("calendar");
  };

  // Handle Print Tasks
  const handlePrintTasks = () => {
    setPrintModalOpen(true);
  };

  /******************************************************
   * RENDER
   ******************************************************/
  return (
    <DndProvider backend={HTML5Backend}>
      <Box
        sx={{
          bgcolor: themeColors.background,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* MAIN CONTENT */}
        <Box sx={{ p: 3 }}>
          {/* Stats & Filters */}
          <Paper
            sx={{
              p: 2.5,
              mb: 4,
              borderRadius: 3,
              backgroundColor: themeColors.card,
              border: isDarkMode
                ? `1px solid ${themeColors.taskBorder}`
                : "none",
            }}
          >
            <Box
              sx={{ mb: 1, display: "flex", justifyContent: "space-between" }}
            >
              <Typography
                variant="h6"
                fontWeight={600}
                color={themeColors.text}
              >
                Dashboard Übersicht
              </Typography>
              <Box sx={{ display: "flex" }}>
                <Button
                  variant="outlined"
                  onClick={handleCalendarView}
                  startIcon={<Calendar className="w-4 h-4" />}
                  sx={{ mr: 1, borderRadius: 2 }}
                >
                  Calendar View
                </Button>

                <Button
                  variant="outlined"
                  onClick={handlePrintTasks}
                  startIcon={<PrintIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Aufgaben drucken
                </Button>
              </Box>
            </Box>
            <Divider sx={{ borderColor: themeColors.taskBorder, mb: 2 }} />

            <Grid container spacing={3}>
              {/* Total Tasks */}
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isDarkMode
                      ? "rgba(67, 97, 238, 0.15)"
                      : "#ebf3ff",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      bgcolor: isDarkMode
                        ? "rgba(67, 97, 238, 0.3)"
                        : "rgba(67, 97, 238, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <AssignmentIcon
                      sx={{ color: themeColors.primary, fontSize: 28 }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color={themeColors.text}
                    >
                      {totalTasks}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={themeColors.textSecondary}
                    >
                      Aufgaben gesamt
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {/* Tasks with ANR */}
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isDarkMode
                      ? "rgba(46, 204, 113, 0.15)"
                      : "#f0fff4",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      bgcolor: isDarkMode
                        ? "rgba(46, 204, 113, 0.3)"
                        : "rgba(46, 204, 113, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <BusinessIcon
                      sx={{ color: themeColors.success, fontSize: 28 }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color={themeColors.text}
                    >
                      {tasksWithAnr}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={themeColors.textSecondary}
                    >
                      Zugewiesene ANRs
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {/* Total Hours */}
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isDarkMode
                      ? "rgba(243, 156, 18, 0.15)"
                      : "#fff8f0",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      bgcolor: isDarkMode
                        ? "rgba(243, 156, 18, 0.3)"
                        : "rgba(243, 156, 18, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <AccessTimeIcon
                      sx={{ color: themeColors.warning, fontSize: 28 }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color={themeColors.text}
                    >
                      {totalHours}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={themeColors.textSecondary}
                    >
                      Gesamtstunden
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              {/* Self Assigned */}
              <Grid item xs={12} sm={6} md={3}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: isDarkMode
                      ? "rgba(52, 152, 219, 0.15)"
                      : "#e3f2fd",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "12px",
                      bgcolor: isDarkMode
                        ? "rgba(52, 152, 219, 0.3)"
                        : "rgba(52, 152, 219, 0.2)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mr: 2,
                    }}
                  >
                    <NotificationsIcon
                      sx={{ color: themeColors.info, fontSize: 28 }}
                    />
                  </Box>
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight="bold"
                      color={themeColors.text}
                    >
                      {selfAssignedTasks}
                    </Typography>
                    <Typography
                      variant="body2"
                      color={themeColors.textSecondary}
                    >
                      Selbst zugewiesen
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* FILTER CONTROLS */}
            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* Left side: user filter */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControl size="small" sx={{ mr: 2, minWidth: 180 }}>
                  <InputLabel id="user-filter-label">Benutzer</InputLabel>
                  <Select
                    labelId="user-filter-label"
                    value={selectedUserFilter}
                    label="Benutzer"
                    onChange={handleUserFilterChange}
                  >
                    {/* My tasks */}
                    <MenuItem value={loggedInUserId}>Meine Aufgaben</MenuItem>
                    {/* All tasks */}
                    <MenuItem value="">Alle Aufgaben</MenuItem>
                    <Divider />
                    {/* Then all Admins/Employees */}
                    {allUsers.map((u) => (
                      <MenuItem key={u._id} value={u._id}>
                        {u.displayName} ({u.userModel})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  placeholder="Suche nach Aufgaben..."
                  variant="outlined"
                  size="small"
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon color="action" sx={{ mr: 1 }} />
                    ),
                  }}
                  sx={{ mr: 2, width: 220 }}
                />

                <Button
                  variant="outlined"
                  startIcon={<FilterIcon />}
                  sx={{ mr: 1, borderRadius: 2 }}
                >
                  Filter
                </Button>
              </Box>

              {/* Right side: refresh & save */}
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<QrCodeIcon />}
                  sx={{ mr: 2, borderRadius: 2 }}
                  onClick={() => {
                    if (tasks.length > 0) {
                      handleShowBarcode(tasks[0]);
                    }
                  }}
                >
                  Scan & Track
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  sx={{ mr: 2, borderRadius: 2 }}
                  onClick={fetchTasks}
                >
                  Aktualisieren
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* KANBAN COLUMNS */}
          <Grid container spacing={2} sx={{ mb: 10, px: 2 }}>
            {columns.map((col) => {
              const columnTasks = tasks.filter((t) => t.column === col.id);
              const isCollapsed = collapsedColumns.includes(col.id);
              const expandedCount = columns.length - collapsedColumns.length;

              // Calculate dynamic grid sizes
              // Collapsed columns take minimal space, expanded columns share remaining space
              const gridSize = isCollapsed
                ? { xs: 12, md: 1, lg: 0.5 }
                : expandedCount === 1
                  ? { xs: 12, md: 11, lg: 11.5 }
                  : expandedCount === 2
                    ? { xs: 12, md: 5.5, lg: 5.75 }
                    : expandedCount === 3
                      ? { xs: 12, md: 6, lg: 3.8 }
                      : { xs: 12, md: 6, lg: 3 };

              return (
                <Grid
                  item
                  xs={gridSize.xs}
                  md={gridSize.md}
                  lg={gridSize.lg}
                  key={col.id}
                  sx={{
                    transition: "all 0.3s ease-in-out",
                  }}
                >
                  <Column
                    column={col}
                    tasks={columnTasks}
                    moveTask={moveTask}
                    onOpenAnr={handleOpenAnr}
                    onEditTask={handleEditTask}
                    onAddTask={handleAddTask}
                    onDeleteTask={handleDeleteTask}
                    onArchiveTask={handleArchiveTask}
                    onStartTimer={handleStartTimer}
                    onStopTimer={handleStopTimer}
                    onResetTimer={handleResetTimer}
                    onShowBarcode={handleShowBarcode}
                    onAddComment={handleAddComment}
                    onEditComment={handleEditComment}
                    onDeleteComment={handleDeleteComment}
                    isDarkMode={isDarkMode}
                    isCollapsed={isCollapsed}
                    onToggleCollapse={() => toggleColumnCollapse(col.id)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Quick Add */}
        <Tooltip title="Schnell Aufgabe hinzufügen">
          <Button
            variant="contained"
            color="primary"
            sx={{
              position: "fixed",
              bottom: 24,
              right: 24,
              borderRadius: "50%",
              width: 60,
              height: 60,
              boxShadow: "0 4px 10px rgba(67, 97, 238, 0.3)",
              backgroundColor: themeColors.primary,
              "&:hover": {
                backgroundColor: "#3651d1",
                boxShadow: "0 6px 15px rgba(67, 97, 238, 0.4)",
              },
            }}
            onClick={() => handleAddTask("backlog")}
          >
            <AddIcon fontSize="large" />
          </Button>
        </Tooltip>

        {/* ANR MODAL */}
        <ANRModal
          open={anrModalOpen}
          onClose={() => setAnrModalOpen(false)}
          onSelect={handleAnrSelect}
          isDarkMode={isDarkMode}
        />

        {/* TASK FORM MODAL */}
        <TaskFormModal
          open={taskFormOpen}
          onClose={() => setTaskFormOpen(false)}
          onSave={handleSaveTask}
          initialTask={currentTask}
          columnId={currentColumnId}
          allUsers={allUsers} // we pass allUsers => can pick any Admin/Employee
          isDarkMode={isDarkMode}
          onAddComment={handleAddComment}
          onEditComment={handleEditComment}
          onDeleteComment={handleDeleteComment}
          emailContext={emailContext} // Pass email context for email-based tasks
        />

        {/* BARCODE MODAL */}
        <TaskBarcodeModal
          open={barcodeModalOpen}
          onClose={() => setBarcodeModalOpen(false)}
          task={selectedTaskForBarcode}
          isDarkMode={isDarkMode}
          onStartTimer={handleStartTimer}
          onStopTimer={handleStopTimer}
          onResetTimer={handleResetTimer}
        />

        {/* PRINT TASKS MODAL */}
        <TaskPrintModal
          open={printModalOpen}
          onClose={() => setPrintModalOpen(false)}
          tasks={tasks}
          allUsers={allUsers}
          isDarkMode={isDarkMode}
        />

        <ToastContainer
          position="bottom-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
        />
      </Box>
    </DndProvider>
  );
};

export default KanbanPage;
